import { BROKER_LINKS } from "@/data/brokers";
import { BROKERS } from '@/mocks/profit';

const PIP_VALUE = 10; // Giá trị mỗi pip cho EUR/USD, 1 lot tiêu chuẩn

export default function BrokerSection() {
  const brokers = BROKERS.map((b) => {
    const spreadCost = b.spreadPips * PIP_VALUE;
    const total = spreadCost + b.commission + b.swap;
    return { ...b, spreadCost, total };
  });
  const minTotal = Math.min(...brokers.map((b) => b.total));

  return (
    <section className="rounded-3xl border border-slate-200/80 bg-white p-6 md:p-8">
      <div className="max-w-2xl">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700 ring-1 ring-violet-100">
          <span className="h-1.5 w-1.5 rounded-full bg-violet-500" />
          Nhà môi giới
        </span>
        <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">
          So sánh chi phí giao dịch
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-slate-500 md:text-base">
          Spread, Commission và Swap chính là ba khoản chi phí mà máy tính lợi nhuận tự động trừ đi. So sánh để chọn
          sàn có chi phí giao dịch thấp nhất.
        </p>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
        {brokers.map((b) => {
          const cheapest = b.total === minTotal;
          return (
            <div
              key={b.name}
              className={`relative flex flex-col rounded-2xl border p-5 ${
                cheapest ? 'border-emerald-300 bg-emerald-50/40' : 'border-slate-200/80 bg-slate-50/50'
              }`}
            >
              {cheapest && (
                <span className="absolute -top-2.5 right-4 rounded-full bg-emerald-600 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                  Chi phí thấp nhất
                </span>
              )}

              <div className="flex items-center gap-3">
                <span
                  className="flex h-11 w-11 items-center justify-center rounded-xl font-display text-base font-bold text-white"
                  style={{ backgroundColor: b.color }}
                >
                  {b.initial}
                </span>
                <div>
                  <p className="text-sm font-bold text-slate-800">{b.name}</p>
                  <p className="text-[11px] text-slate-400">{b.regulation}</p>
                </div>
              </div>

              <div className="mt-4 space-y-2.5">
                <CostRow label="Spread" value={`${b.spreadPips.toFixed(1)} pip`} hint={`≈ $${b.spreadCost.toFixed(1)}`} />
                <CostRow label="Commission" value={b.commission === 0 ? 'Miễn phí' : `$${b.commission} / lot`} />
                <CostRow label="Swap" value={`$${b.swap} / đêm`} />
              </div>

              <div className={`mt-5 rounded-xl px-4 py-3 ${cheapest ? 'bg-emerald-600' : 'bg-white'}`}>
                <p className={`text-[11px] font-medium ${cheapest ? 'text-emerald-100' : 'text-slate-400'}`}>
                  Chi phí ước tính · EUR/USD 1 lot
                </p>
                <p className={`mt-0.5 font-mono text-xl font-bold tabular-nums ${cheapest ? 'text-white' : 'text-slate-800'}`}>
                  ${b.total.toFixed(1)}
                </p>
              </div>

              <a href={BROKER_LINKS[b.name]||'#'}
                className={`mt-4 w-full text-center whitespace-nowrap rounded-lg px-3 py-2.5 text-sm font-semibold text-white transition-colors ${
                  cheapest ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-violet-600 hover:bg-violet-700'
                }`}
              >
                Mở tài khoản
              </a>
            </div>
          );
        })}
      </div>

      <p className="mt-6 text-[11px] leading-relaxed text-slate-400">
        Chi phí được ước tính cho EUR/USD 1 lot tiêu chuẩn, giữ lệnh qua 1 đêm. Giá trị thực tế có thể thay đổi tùy
        theo loại tài khoản, cặp tiền và điều kiện thị trường. Công cụ này chỉ nhằm mục đích thông tin, không cấu thành
        lời khuyên giao dịch.
      </p>
    </section>
  );
}

function CostRow({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="flex items-center justify-between gap-2 border-b border-slate-200/60 pb-2 text-xs">
      <span className="text-slate-400">{label}</span>
      <span className="text-right">
        <span className="font-medium text-slate-700">{value}</span>
        {hint && <span className="ml-1.5 text-slate-400">{hint}</span>}
      </span>
    </div>
  );
}